/**
 * SalespersonRow Component
 *
 * Expandable row displaying a single salesperson's performance.
 * Collapsed: rank, name, total gross, units breakdown.
 * Expanded: front gross, holdback, F&I gross with per-unit averages.
 */

import React from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Salesperson } from './types';
import { formatCurrency, formatPerUnit } from './format';
import { UnitsBreakdown } from './UnitsBreakdown';

interface SalespersonRowProps {
  salesperson: Salesperson;
  isExpanded: boolean;
  onToggle: () => void;
  rank: number;
}

const DRILL_DOWN_ROWS: Array<{
  label: string;
  grossKey: keyof Salesperson;
  avgKey: keyof Salesperson;
}> = [
  { label: 'Front Gross', grossKey: 'frontGross', avgKey: 'avgFrontGrossPerUnit' },
  { label: 'Holdback', grossKey: 'holdback', avgKey: 'avgHoldbackPerUnit' },
  { label: 'F&I Gross', grossKey: 'fiGross', avgKey: 'avgFiGrossPerUnit' },
];

export const SalespersonRow: React.FC<SalespersonRowProps> = ({
  salesperson,
  isExpanded,
  onToggle,
  rank,
}) => {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div
        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-gray-400 transition-transform duration-200">
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
          <span className="text-xs text-gray-400 font-medium w-5">{rank}</span>
          <span className="font-medium text-gray-900 truncate">
            {salesperson.salespersonName}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <span className="text-lg font-bold text-blue-600">
              {formatCurrency(salesperson.totalGross)}
            </span>
            <div className="text-xs text-gray-500">
              Avg/Unit: {formatPerUnit(salesperson.avgTotalGrossPerUnit)}
            </div>
          </div>
          <UnitsBreakdown
            newUnits={salesperson.newUnits}
            swapUnits={salesperson.swapUnits ?? 0}
            usedUnits={salesperson.usedUnits}
            wholesaleUnits={salesperson.wholesaleUnits ?? 0}
            totalUnits={salesperson.totalUnits}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="ml-12 mr-3 mb-3 bg-gray-50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
                <th className="text-left py-2 px-3 font-medium">Metric</th>
                <th className="text-right py-2 px-3 font-medium">Total</th>
                <th className="text-right py-2 px-3 font-medium">Per Unit</th>
              </tr>
            </thead>
            <tbody>
              {DRILL_DOWN_ROWS.map((row) => (
                <tr key={row.label} className="border-t border-gray-200 hover:bg-white">
                  <td className="py-2 px-3 text-gray-700">{row.label}</td>
                  <td className="py-2 px-3 text-right text-gray-900 font-medium">
                    {formatCurrency(salesperson[row.grossKey] as number)}
                  </td>
                  <td className="py-2 px-3 text-right text-gray-600">
                    {formatPerUnit(salesperson[row.avgKey] as number | null)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 bg-gray-100 font-medium">
                <td className="py-2 px-3 text-gray-700">Total</td>
                <td className="py-2 px-3 text-right text-blue-600 font-bold">
                  {formatCurrency(salesperson.totalGross)}
                </td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {formatPerUnit(salesperson.avgTotalGrossPerUnit)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
